import type {
  PcfValidationReport,
  SeverityLevel,
  ValidationCheckResult,
  ValidationDefinition,
  ValidationIssue,
} from '../types';
import type { ValidationIssueFamily } from './validationIssueFamilies';
import {
  CUSTOM_VALIDATION_ID_BASE,
  CONDITION_TARGET_LABELS,
  type ValidationConditionClause,
  type ValidationConditionField,
  type ValidationConditionGroup,
  type ValidationConditionLogic,
  type ValidationConditionOperator,
  type ValidationConditionOutcome,
  type ValidationConditionSet,
  type ValidationConditionSetDraft,
  type ValidationConditionTargetKey,
} from '../../../shared/stores/usePcfValidationStore';

export const VALIDATION_CONDITION_FIELD_OPTIONS: Array<{
  value: ValidationConditionField;
  label: string;
}> = [
  { value: 'category', label: 'Category' },
  { value: 'componentType', label: 'Component type' },
  { value: 'familyName', label: 'Family name' },
  { value: 'system', label: 'System' },
  { value: 'standard', label: 'Standard' },
  { value: 'parameterName', label: 'Parameter name' },
  { value: 'parameterValue', label: 'Parameter value' },
  { value: 'exportFieldValue', label: 'Export field value' },
  { value: 'classification', label: 'Classification' },
  { value: 'severity', label: 'Severity' },
  { value: 'status', label: 'Status' },
  { value: 'namePattern', label: 'Name pattern' },
];

export const VALIDATION_CONDITION_OPERATOR_OPTIONS: Array<{
  value: ValidationConditionOperator;
  label: string;
}> = [
  { value: 'equals', label: 'equals' },
  { value: 'notEquals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'startsWith', label: 'starts with' },
  { value: 'matches', label: 'matches regex' },
  { value: 'isEmpty', label: 'is empty' },
  { value: 'isNotEmpty', label: 'is not empty' },
];

const FIELD_LABELS = VALIDATION_CONDITION_FIELD_OPTIONS.reduce<Record<ValidationConditionField, string>>(
  (accumulator, option) => {
    accumulator[option.value] = option.label;
    return accumulator;
  },
  {} as Record<ValidationConditionField, string>
);

const OPERATOR_LABELS =
  VALIDATION_CONDITION_OPERATOR_OPTIONS.reduce<Record<ValidationConditionOperator, string>>(
    (accumulator, option) => {
      accumulator[option.value] = option.label;
      return accumulator;
    },
    {} as Record<ValidationConditionOperator, string>
  );

export interface ValidationConditionTargetSummary {
  key: ValidationConditionTargetKey;
  label: string;
  candidateCount: number;
}

export interface ValidationConditionCandidateItem {
  id: string;
  targetKey: ValidationConditionTargetKey;
  label: string;
  category: string;
  componentType: string;
  familyName: string;
  system: string;
  standard: string;
  parameterName: string;
  parameterValue: string;
  exportFieldValue: string;
  classification: string;
  severity: string;
  status: string;
  source: string;
  rawIssue: ValidationIssue;
}

export interface ValidationConditionStudioEntryResolution {
  action: 'open-existing' | 'create-new';
  targetKey: ValidationConditionTargetKey;
  conditionSetId: string | null;
  candidateCount: number;
  matchCount: number;
}

const SEVERITY_RANK: Record<SeverityLevel, number> = {
  Critical: 3,
  Error: 2,
  Warning: 1,
  Info: 0,
};

export interface ValidationConditionClauseResult {
  clauseId: string;
  passed: boolean;
  label: string;
  actualValue: string;
  expectedValue: string;
  field: ValidationConditionField;
  operator: ValidationConditionOperator;
  reason: string;
  invalid: boolean;
}

export interface ValidationConditionClauseDiagnostic {
  clauseId: string;
  level: 'error' | 'warning';
  message: string;
}

export interface ValidationConditionGroupDiagnostic {
  groupId: string;
  level: 'error' | 'warning';
  message: string;
}

export interface ValidationConditionBuilderDiagnostic {
  id: string;
  level: 'warning' | 'info';
  message: string;
}

export interface ValidationConditionGroupResult {
  groupId: string;
  passed: boolean;
  logic: ValidationConditionLogic;
  clauseResults: ValidationConditionClauseResult[];
  reason: string;
}

export interface ValidationConditionSetEvaluation {
  matched: boolean;
  groupResults: ValidationConditionGroupResult[];
  conditionSetId?: string;
}

export interface ValidationConditionResolution {
  itemId: string;
  matched: boolean;
  winningConditionSetId: string | null;
  winningConditionSetName: string | null;
  outcome: ValidationConditionOutcome['type'] | 'include';
  severity: string | null;
  reason: string;
  shadowedByHigherPriority: boolean;
}

export interface ValidationConditionConflict {
  id: string;
  level: 'warning' | 'error';
  title: string;
  description: string;
  scope?: 'builder' | 'group' | 'clause' | 'outcome';
  groupId?: string;
  clauseIds?: string[];
}

export interface ValidationConditionDraftDiagnostics {
  clauseDiagnostics: ValidationConditionClauseDiagnostic[];
  groupDiagnostics: ValidationConditionGroupDiagnostic[];
  builderDiagnostics: ValidationConditionBuilderDiagnostic[];
  blockingClauseCount: number;
  blockingGroupCount: number;
  warningCount: number;
  hasBlockingIssues: boolean;
}

function extractInstanceLabel(rawValue: string | undefined, fallbackId: string): string {
  const trimmed = (rawValue || '').trim();
  if (!trimmed) {
    return fallbackId;
  }

  return trimmed
    .replace(/\s*\[\d+\]\s*/g, ' ')
    .replace(/\s*\(ID:\s*\d+\)\s*/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function extractComponentTypeLabel(instanceLabel: string): string {
  if (instanceLabel.includes(':')) {
    return instanceLabel.split(':')[0].trim() || instanceLabel;
  }

  if (instanceLabel.includes(' - ')) {
    return instanceLabel.split(' - ')[0].trim() || instanceLabel;
  }

  return instanceLabel;
}

function inferTargetKey(issue: ValidationIssue): ValidationConditionTargetKey {
  const upperMessage = issue.Message.toUpperCase();
  const upperCategory = issue.Category.toUpperCase();

  if (upperCategory.includes('CONNECT') || upperMessage.includes('CONNECTOR') || upperMessage.includes('END-TYPE')) {
    return 'connectors';
  }

  if (upperMessage.includes('MAPPED') || upperMessage.includes('MAPPING')) {
    return 'mapping';
  }

  if (upperMessage.includes('OBJECT-TYPE') || upperMessage.includes('SKEY') || upperMessage.includes('PIPING-SPEC')) {
    return 'required-export';
  }

  if (upperCategory.includes('DATA QUALITY') || upperCategory.includes('SUMMARY')) {
    return 'properties';
  }

  return 'classification';
}

function formatValueDisplay(value: string): string {
  return value.trim().length > 0 ? value : '[missing value]';
}

function formatActualValueDisplay(value: string): string {
  return value.trim().length > 0 ? value : '[missing value]';
}

function buildOutcomeLabel(outcome: ValidationConditionOutcome): string {
  switch (outcome.type) {
    case 'exclude':
      return 'exclude from validation';
    case 'include':
      return 'include in validation';
    case 'downgrade':
      return `downgrade severity${outcome.severity ? ` to ${outcome.severity}` : ''}`;
    case 'warningOnly':
      return `mark as warning only${outcome.severity ? ` (${outcome.severity})` : ''}`;
    case 'routeAlternate':
      return outcome.routeTo ? `route to ${outcome.routeTo}` : 'route to an alternate validation path';
    case 'requireAdditional':
      return 'require an additional condition set';
    default:
      return 'include in validation';
  }
}

function buildDefaultOutcomeLabel(defaultOutcome: ValidationConditionSetDraft['defaultOutcome']): string {
  return defaultOutcome === 'include' ? 'default include' : 'default exclude';
}

export function buildConditionClauseLabel(clause: ValidationConditionClause): string {
  const valueLabel =
    clause.operator === 'isEmpty' || clause.operator === 'isNotEmpty'
      ? ''
      : ` ${formatValueDisplay(clause.value.trim())}`;
  const base = `${FIELD_LABELS[clause.field]} ${OPERATOR_LABELS[clause.operator]}${valueLabel}`.trim();
  return clause.negate ? `NOT ${base}` : base;
}

function operatorRequiresValue(operator: ValidationConditionOperator): boolean {
  return !['isEmpty', 'isNotEmpty'].includes(operator);
}

function getCandidateFieldValue(
  candidate: ValidationConditionCandidateItem,
  field: ValidationConditionField
): string {
  switch (field) {
    case 'category':
      return candidate.category;
    case 'componentType':
      return candidate.componentType;
    case 'familyName':
      return candidate.familyName;
    case 'system':
      return candidate.system;
    case 'standard':
      return candidate.standard;
    case 'parameterName':
      return candidate.parameterName;
    case 'parameterValue':
      return candidate.parameterValue;
    case 'exportFieldValue':
      return candidate.exportFieldValue;
    case 'classification':
      return candidate.classification;
    case 'severity':
      return candidate.severity;
    case 'status':
      return candidate.status;
    case 'namePattern':
      return candidate.label;
    default:
      return '';
  }
}

function evaluateOperator(operator: ValidationConditionOperator, actual: string, expected: string): boolean {
  const normalizedActual = actual.toLowerCase();
  const normalizedExpected = expected.toLowerCase();

  switch (operator) {
    case 'equals':
      return normalizedActual === normalizedExpected;
    case 'notEquals':
      return normalizedActual !== normalizedExpected;
    case 'contains':
      return normalizedActual.includes(normalizedExpected);
    case 'startsWith':
      return normalizedActual.startsWith(normalizedExpected);
    case 'matches':
      try {
        return new RegExp(expected, 'i').test(actual);
      } catch {
        return false;
      }
    case 'isEmpty':
      return actual.trim().length === 0;
    case 'isNotEmpty':
      return actual.trim().length > 0;
    default:
      return false;
  }
}

function buildClauseReason(params: {
  clause: ValidationConditionClause;
  actual: string;
  passed: boolean;
  basePassed: boolean;
  blockingError: string | null;
}): string {
  const { clause, actual, passed, basePassed, blockingError } = params;

  if (blockingError) {
    if (blockingError === 'This operator requires a value.') {
      return 'failed because the rule value is missing.';
    }

    if (blockingError === 'Regex is invalid.') {
      return 'failed because the regex is invalid.';
    }

    return blockingError;
  }

  if (clause.negate) {
    return passed
      ? 'passed because the negated condition did not match.'
      : 'failed because the item still matched a negated condition.';
  }

  if (clause.operator === 'isEmpty') {
    return passed
      ? 'passed because the item value is missing.'
      : `failed because the item value was ${formatActualValueDisplay(actual)}.`;
  }

  if (clause.operator === 'isNotEmpty') {
    return passed
      ? `passed because the item value was ${formatActualValueDisplay(actual)}.`
      : 'failed because the item value is missing.';
  }

  if (actual.trim().length === 0 && !basePassed) {
    return 'failed because the item value is missing.';
  }

  const actualValue = formatActualValueDisplay(actual);
  const expectedValue = formatValueDisplay(clause.value.trim());

  switch (clause.operator) {
    case 'equals':
      return passed
        ? `passed because the item value matched ${expectedValue}.`
        : `failed because the item value was ${actualValue}, not ${expectedValue}.`;
    case 'notEquals':
      return passed
        ? `passed because the item value was ${actualValue}, not ${expectedValue}.`
        : `failed because the item value matched ${expectedValue}.`;
    case 'contains':
      return passed
        ? `passed because the item value ${actualValue} contains ${expectedValue}.`
        : `failed because the item value ${actualValue} does not contain ${expectedValue}.`;
    case 'startsWith':
      return passed
        ? `passed because the item value ${actualValue} starts with ${expectedValue}.`
        : `failed because the item value ${actualValue} does not start with ${expectedValue}.`;
    case 'matches':
      return passed
        ? `passed because the item value ${actualValue} matches the regex ${expectedValue}.`
        : `failed because the item value ${actualValue} does not match the regex ${expectedValue}.`;
    default:
      return passed ? 'passed.' : 'failed.';
  }
}

function buildGroupReason(group: ValidationConditionGroupResult): string {
  const passedClauses = group.clauseResults.filter((result) => result.passed).length;
  const failedClauses = group.clauseResults.length - passedClauses;

  if (group.logic === 'all') {
    return group.passed
      ? 'All clauses passed, so this group passed.'
      : `${failedClauses} clause${failedClauses === 1 ? '' : 's'} failed, so this ALL group failed.`;
  }

  return group.passed
    ? `${passedClauses} clause${passedClauses === 1 ? '' : 's'} passed, so this ANY group passed.`
    : 'No clauses passed, so this ANY group failed.';
}

export function mapValidationFamilyToConditionTargetKey(
  family: ValidationIssueFamily
): ValidationConditionTargetKey {
  switch (family.category) {
    case 'required-export':
      return 'required-export';
    case 'connectors':
      return 'connectors';
    case 'mapping':
      return 'mapping';
    case 'properties':
      return 'properties';
    default:
      return 'classification';
  }
}

export function mapValidationDefinitionToConditionTargetKey(
  validation: ValidationDefinition
): ValidationConditionTargetKey {
  const context = [validation.Title, validation.Description, validation.Category]
    .join(' ')
    .toUpperCase();

  if (context.includes('MAPPING') || context.includes('MAPPED')) {
    return 'mapping';
  }

  if (
    context.includes('CONNECTOR') ||
    context.includes('CONNECTIVITY') ||
    context.includes('END-TYPE') ||
    context.includes('SYSTEM BREAK') ||
    context.includes('PIPE SIZE')
  ) {
    return 'connectors';
  }

  if (
    context.includes('CLASSIFICATION') ||
    context.includes('UNSUPPORTED') ||
    context.includes('CATEGORY MATCH') ||
    context.includes('READINESS')
  ) {
    return 'classification';
  }

  if (
    validation.Category === 'Summary' ||
    context.includes('PROPERTY') ||
    context.includes('WORKFLOW') ||
    context.includes('PROJECT') ||
    context.includes('MATERIAL') ||
    context.includes('PRESSURE') ||
    context.includes('DESIGN STANDARD') ||
    context.includes('INSULATION') ||
    context.includes('TRACING') ||
    context.includes('ITEM-DESCRIPTION')
  ) {
    return 'properties';
  }

  return 'required-export';
}

export function resolveConditionStudioEntryForValidation(params: {
  validation: ValidationDefinition;
  conditionSets: ValidationConditionSet[];
}): ValidationConditionStudioEntryResolution {
  const { validation, conditionSets } = params;
  const targetKey = mapValidationDefinitionToConditionTargetKey(validation);
  const existingConditionSet =
    getBoundConditionSetsForValidation(validation.Id, targetKey, conditionSets)[0] ?? null;
  if (existingConditionSet) {
    return {
      action: 'open-existing',
      targetKey,
      conditionSetId: existingConditionSet.id,
      candidateCount: 0,
      matchCount: 1,
    };
  }

  return {
    action: 'create-new',
    targetKey,
    conditionSetId: null,
    candidateCount: 0,
    matchCount: 0,
  };
}

function resolveConditionSetBoundValidationId(conditionSet: ValidationConditionSet): number | null {
  return conditionSet.draft.validationId ?? conditionSet.published?.validationId ?? null;
}

export function getBoundConditionSetsForValidation(
  validationId: number,
  targetKey: ValidationConditionTargetKey,
  conditionSets: ValidationConditionSet[]
): ValidationConditionSet[] {
  return conditionSets
    .filter(
      (conditionSet) =>
        !conditionSet.archived &&
        conditionSet.targetKey === targetKey &&
        resolveConditionSetBoundValidationId(conditionSet) === validationId
    )
    .sort((left, right) => {
      if (left.draft.priority !== right.draft.priority) {
        return left.draft.priority - right.draft.priority;
      }

      const specificityDelta =
        calculateConditionSetSpecificity(right.draft) - calculateConditionSetSpecificity(left.draft);
      if (specificityDelta !== 0) {
        return specificityDelta;
      }

      return left.draft.name.localeCompare(right.draft.name);
    });
}

export function getUnassignedConditionSetsForTarget(
  targetKey: ValidationConditionTargetKey,
  conditionSets: ValidationConditionSet[]
): ValidationConditionSet[] {
  return conditionSets
    .filter(
      (conditionSet) =>
        !conditionSet.archived &&
        conditionSet.targetKey === targetKey &&
        resolveConditionSetBoundValidationId(conditionSet) == null
    )
    .sort((left, right) => {
      if (left.draft.priority !== right.draft.priority) {
        return left.draft.priority - right.draft.priority;
      }

      return left.draft.name.localeCompare(right.draft.name);
    });
}

export function buildValidationConditionCandidateItems(
  report: PcfValidationReport | null
): ValidationConditionCandidateItem[] {
  if (!report) {
    return [];
  }

  return report.CheckResults.flatMap((check) =>
    check.Issues.flatMap((issue, issueIndex) => {
      const pairCount = Math.max(issue.ElementIds.length, issue.AffectedElements.length, 1);
      return Array.from({ length: pairCount }, (_, pairIndex) => {
        const elementId = issue.ElementIds[pairIndex] || issue.ElementIds[0] || `item-${issueIndex}`;
        const rawLabel = issue.AffectedElements[pairIndex] || issue.AffectedElements[0] || `Element ${elementId}`;
        const label = extractInstanceLabel(rawLabel, `Element ${elementId}`);
        const parameterName = issue.AdditionalData?.parameterName || '';
        const parameterValue = issue.AdditionalData?.currentValue || '';

        return {
          id: `${issue.ValidationId}:${pairIndex}:${elementId}`,
          targetKey: inferTargetKey(issue),
          label,
          category: issue.Category,
          componentType: extractComponentTypeLabel(label),
          familyName: label,
          system: issue.AdditionalData?.systemName || report.DocumentName || 'Current model',
          standard: issue.AdditionalData?.standard || 'ISO-10628',
          parameterName,
          parameterValue,
          exportFieldValue: parameterValue,
          classification: issue.AdditionalData?.classification || extractComponentTypeLabel(label),
          severity: issue.Severity,
          status: check.Status,
          source: issue.AdditionalData?.source || check.Title,
          rawIssue: issue,
        } satisfies ValidationConditionCandidateItem;
      });
    })
  );
}

function buildConditionStudioIssueSignature(issue: ValidationIssue): string {
  return JSON.stringify({
    validationId: issue.ValidationId,
    title: issue.ValidationTitle || '',
    message: issue.Message || '',
    category: issue.Category || '',
    recommendation: issue.Recommendation || '',
    elementIds: issue.ElementIds || [],
    affectedElements: issue.AffectedElements || [],
    parameterName: issue.AdditionalData?.parameterName || '',
    currentValue: issue.AdditionalData?.currentValue || '',
    systemName: issue.AdditionalData?.systemName || '',
  });
}

function normalizeConditionStudioToken(value: string | undefined): string {
  return (value || '').trim().toUpperCase().replace(/\s+/g, ' ');
}

function buildFamilyScopedCandidates(
  candidates: ValidationConditionCandidateItem[],
  family: ValidationIssueFamily,
  targetKey: ValidationConditionTargetKey
): ValidationConditionCandidateItem[] {
  const familyIssueSignatures = new Set(
    family.rawIssues.map((issue) => buildConditionStudioIssueSignature(issue))
  );
  const impactedElementIds = new Set(family.impactedElementIds);
  const affectedTokens = new Set(
    [
      ...family.affectedItems,
      ...family.examples.map((example) => example.label),
      ...family.failingInstances.map((instance) => instance.label),
      ...family.failingTypes.map((type) => type.componentType),
    ]
      .map((value) => normalizeConditionStudioToken(value))
      .filter(Boolean)
  );

  return candidates.filter((candidate) => {
    if (candidate.targetKey !== targetKey) {
      return false;
    }

    if (familyIssueSignatures.size > 0) {
      return familyIssueSignatures.has(buildConditionStudioIssueSignature(candidate.rawIssue));
    }

    if (!family.validationIds.includes(candidate.rawIssue.ValidationId)) {
      return false;
    }

    if (candidate.rawIssue.ElementIds.some((elementId) => impactedElementIds.has(elementId))) {
      return true;
    }

    return [
      candidate.label,
      candidate.componentType,
      candidate.familyName,
      candidate.parameterName,
      candidate.source,
    ]
      .map((value) => normalizeConditionStudioToken(value))
      .some((value) => affectedTokens.has(value));
  });
}

function calculateConditionSetSpecificity(draft: ValidationConditionSetDraft): number {
  return draft.groups.reduce((score, group) => {
    const completeClauses = group.clauses.filter(
      (clause) => !validateClause(clause).some((diagnostic) => diagnostic.level === 'error')
    );
    return score + completeClauses.length;
  }, 0);
}

export function resolveConditionStudioEntryForFamily(params: {
  report: PcfValidationReport | null;
  family: ValidationIssueFamily;
  conditionSets: ValidationConditionSet[];
}): ValidationConditionStudioEntryResolution {
  const { report, family, conditionSets } = params;
  const targetKey = mapValidationFamilyToConditionTargetKey(family);
  const scopedCandidates = buildFamilyScopedCandidates(
    buildValidationConditionCandidateItems(report),
    family,
    targetKey
  );

  const rankedConditionSets = conditionSets
    .map((conditionSet, index) => ({
      conditionSet,
      index,
    }))
    .filter(({ conditionSet }) => !conditionSet.archived && conditionSet.targetKey === targetKey)
    .map(({ conditionSet, index }) => ({
      conditionSet,
      index,
      matchCount: scopedCandidates.filter((candidate) =>
        evaluateConditionDraftForCandidate(conditionSet.draft, candidate).matched
      ).length,
      specificity: calculateConditionSetSpecificity(conditionSet.draft),
    }))
    .sort((left, right) => {
      if (right.matchCount !== left.matchCount) {
        return right.matchCount - left.matchCount;
      }

      if (left.conditionSet.draft.priority !== right.conditionSet.draft.priority) {
        return left.conditionSet.draft.priority - right.conditionSet.draft.priority;
      }

      if (right.specificity !== left.specificity) {
        return right.specificity - left.specificity;
      }

      return left.index - right.index;
    });

  const bestMatch = rankedConditionSets[0];
  if (bestMatch && bestMatch.matchCount > 0) {
    return {
      action: 'open-existing',
      targetKey,
      conditionSetId: bestMatch.conditionSet.id,
      candidateCount: scopedCandidates.length,
      matchCount: bestMatch.matchCount,
    };
  }

  return {
    action: 'create-new',
    targetKey,
    conditionSetId: null,
    candidateCount: scopedCandidates.length,
    matchCount: 0,
  };
}

export function buildValidationConditionTargetSummaries(
  candidates: ValidationConditionCandidateItem[]
): ValidationConditionTargetSummary[] {
  return (Object.keys(CONDITION_TARGET_LABELS) as ValidationConditionTargetKey[]).map((key) => ({
    key,
    label: CONDITION_TARGET_LABELS[key],
    candidateCount: candidates.filter((candidate) => candidate.targetKey === key).length,
  }));
}

function validateClause(clause: ValidationConditionClause): ValidationConditionClauseDiagnostic[] {
  const diagnostics: ValidationConditionClauseDiagnostic[] = [];
  const valueRequired = operatorRequiresValue(clause.operator);

  if (valueRequired && clause.value.trim().length === 0) {
    diagnostics.push({
      clauseId: clause.id,
      level: 'error',
      message: 'This operator requires a value.',
    });
  }

  if (
    ['severity', 'status'].includes(clause.field) &&
    ['contains', 'startsWith', 'matches'].includes(clause.operator)
  ) {
    diagnostics.push({
      clauseId: clause.id,
      level: 'warning',
      message: 'This operator is broad for enum-like fields and may be hard to reason about.',
    });
  }

  if (clause.operator === 'matches' && clause.value.trim().length > 0) {
    try {
      new RegExp(clause.value, 'i');
    } catch {
      diagnostics.push({
        clauseId: clause.id,
        level: 'error',
        message: 'Regex is invalid.',
      });
    }
  }

  return diagnostics;
}

export function validateConditionDraft(
  draft: ValidationConditionSetDraft,
  candidates?: ValidationConditionCandidateItem[]
): ValidationConditionDraftDiagnostics {
  const clauseDiagnostics = draft.groups.flatMap((group) =>
    group.clauses.flatMap((clause) => validateClause(clause))
  );
  const groupDiagnostics: ValidationConditionGroupDiagnostic[] = [];
  const builderDiagnostics: ValidationConditionBuilderDiagnostic[] = [];

  draft.groups.forEach((group) => {
    if (group.clauses.length === 0) {
      groupDiagnostics.push({
        groupId: group.id,
        level: 'error',
        message: 'Condition group must contain at least one clause.',
      });
    }

    const completeClauses = group.clauses.filter(
      (clause) => !validateClause(clause).some((diagnostic) => diagnostic.level === 'error')
    );
    if (group.clauses.length > 0 && completeClauses.length === 0) {
      groupDiagnostics.push({
        groupId: group.id,
        level: 'warning',
        message: 'No valid clauses remain in this group.',
      });
    }
  });

  if (candidates && candidates.length > 0) {
    const matchedCandidates = candidates.filter((candidate) =>
      evaluateConditionDraftForCandidate(draft, candidate).matched
    );
    if (matchedCandidates.length === 0) {
      builderDiagnostics.push({
        id: 'sample-coverage-warning',
        level: 'warning',
        message: 'Draft does not currently match any sampled items.',
      });
    }
  }

  const blockingClauseCount = clauseDiagnostics.filter((diagnostic) => diagnostic.level === 'error').length;
  const blockingGroupCount = groupDiagnostics.filter((diagnostic) => diagnostic.level === 'error').length;
  const warningCount =
    clauseDiagnostics.filter((diagnostic) => diagnostic.level === 'warning').length +
    groupDiagnostics.filter((diagnostic) => diagnostic.level === 'warning').length +
    builderDiagnostics.filter((diagnostic) => diagnostic.level === 'warning').length;

  return {
    clauseDiagnostics,
    groupDiagnostics,
    builderDiagnostics,
    blockingClauseCount,
    blockingGroupCount,
    warningCount,
    hasBlockingIssues: blockingClauseCount > 0 || blockingGroupCount > 0,
  };
}

function evaluateClause(
  clause: ValidationConditionClause,
  candidate: ValidationConditionCandidateItem
): ValidationConditionClauseResult {
  const actual = getCandidateFieldValue(candidate, clause.field);
  const blockingError =
    validateClause(clause).find((diagnostic) => diagnostic.level === 'error')?.message || null;
  const basePassed = blockingError ? false : evaluateOperator(clause.operator, actual, clause.value);
  const passed = blockingError ? false : clause.negate ? !basePassed : basePassed;

  return {
    clauseId: clause.id,
    passed,
    label: buildConditionClauseLabel(clause),
    actualValue: actual,
    expectedValue: clause.value,
    field: clause.field,
    operator: clause.operator,
    reason: buildClauseReason({
      clause,
      actual,
      passed,
      basePassed,
      blockingError,
    }),
    invalid: Boolean(blockingError),
  };
}

function evaluateGroup(
  group: ValidationConditionGroup,
  candidate: ValidationConditionCandidateItem
): ValidationConditionGroupResult {
  const clauseResults = group.clauses.map((clause) => evaluateClause(clause, candidate));
  const passed =
    group.logic === 'all'
      ? clauseResults.every((result) => result.passed)
      : clauseResults.some((result) => result.passed);

  const result: ValidationConditionGroupResult = {
    groupId: group.id,
    passed,
    logic: group.logic,
    clauseResults,
    reason: '',
  };

  return {
    ...result,
    reason: buildGroupReason(result),
  };
}

export function evaluateConditionDraftForCandidate(
  draft: ValidationConditionSetDraft,
  candidate: ValidationConditionCandidateItem
): ValidationConditionSetEvaluation {
  const groupResults = draft.groups.map((group) => evaluateGroup(group, candidate));
  const matched =
    draft.rootLogic === 'all'
      ? groupResults.every((result) => result.passed)
      : groupResults.some((result) => result.passed);

  return {
    matched,
    groupResults,
  };
}

function resolveCandidateByConditionSets(
  candidate: ValidationConditionCandidateItem,
  conditionSets: Array<ValidationConditionSet & { version: ValidationConditionSetDraft | null }>
): ValidationConditionResolution {
  const relevantSets = conditionSets
    .filter((conditionSet) => conditionSet.targetKey === candidate.targetKey && conditionSet.version?.enabled)
    .sort((left, right) => (left.version?.priority || 0) - (right.version?.priority || 0));

  const matchingSets = relevantSets
    .map((conditionSet) => ({
      conditionSet,
      evaluation: conditionSet.version
        ? evaluateConditionDraftForCandidate(conditionSet.version, candidate)
        : { matched: false, groupResults: [] },
    }))
    .filter((result) => result.evaluation.matched);

  const winning = matchingSets[0];
  if (!winning || !winning.conditionSet.version) {
    return {
      itemId: candidate.id,
      matched: false,
      winningConditionSetId: null,
      winningConditionSetName: null,
      outcome: 'include',
      severity: null,
      reason: 'No matching condition set. Default validation behavior remains active.',
      shadowedByHigherPriority: false,
    };
  }

  return {
    itemId: candidate.id,
    matched: true,
    winningConditionSetId: winning.conditionSet.id,
    winningConditionSetName: winning.conditionSet.version.name,
    outcome: winning.conditionSet.version.outcome.type,
    severity: winning.conditionSet.version.outcome.severity || null,
    reason: `${winning.conditionSet.version.name} matched first at priority ${winning.conditionSet.version.priority}.`,
    shadowedByHigherPriority: matchingSets.length > 1,
  };
}

function resolvePublishedConditionSets(conditionSets: ValidationConditionSet[]) {
  return conditionSets.map((conditionSet) => ({
    ...conditionSet,
    version: conditionSet.published,
  }));
}

function resolveIssueSeverityOverride(
  issue: ValidationIssue,
  resolutions: ValidationConditionResolution[]
): SeverityLevel {
  let nextSeverity = issue.Severity;

  for (const resolution of resolutions) {
    let requestedSeverity: SeverityLevel | null = null;
    if (resolution.outcome === 'warningOnly') {
      requestedSeverity = 'Warning';
    } else if (resolution.outcome === 'downgrade') {
      requestedSeverity = resolution.severity === 'info' ? 'Info' : 'Warning';
    }

    if (!requestedSeverity) {
      continue;
    }

    if (SEVERITY_RANK[requestedSeverity] < SEVERITY_RANK[nextSeverity]) {
      nextSeverity = requestedSeverity;
    }
  }

  return nextSeverity;
}

function resolveCheckStatusFromIssues(issues: ValidationIssue[]): ValidationCheckResult['Status'] {
  if (issues.length === 0) {
    return 'Passed';
  }

  if (issues.some((issue) => issue.Severity === 'Critical' || issue.Severity === 'Error')) {
    return 'Failed';
  }

  return 'Warning';
}

function resolveCustomValidationSeverity(validation: ValidationDefinition): SeverityLevel {
  if (
    validation.DefaultSeverity === 'Critical' ||
    validation.DefaultSeverity === 'Error' ||
    validation.DefaultSeverity === 'Warning' ||
    validation.DefaultSeverity === 'Info'
  ) {
    return validation.DefaultSeverity;
  }

  return 'Warning';
}

function resolvePublishedConditionSetValidationId(
  conditionSet: ValidationConditionSet & { version: ValidationConditionSetDraft | null }
): number | null {
  return conditionSet.version?.validationId ?? conditionSet.draft.validationId ?? conditionSet.published?.validationId ?? null;
}

export function applyPublishedConditionSetsToCheckResults(params: {
  checkResults: ValidationCheckResult[] | undefined;
  conditionSets: ValidationConditionSet[];
  documentName?: string;
  customValidationIds?: number[];
}): ValidationCheckResult[] {
  const { checkResults, conditionSets, documentName, customValidationIds = [] } = params;
  if (!checkResults || checkResults.length === 0) {
    return [];
  }

  const customValidationIdSet = new Set(customValidationIds);
  const publishedSets = resolvePublishedConditionSets(conditionSets)
    .filter((conditionSet) => conditionSet.version?.enabled)
    .filter((conditionSet) => {
      const validationId = resolvePublishedConditionSetValidationId(conditionSet);
      return validationId == null || !customValidationIdSet.has(validationId);
    });
  if (publishedSets.length === 0) {
    return checkResults;
  }

  const candidateReport: PcfValidationReport = {
    DocumentName: documentName || 'Current model',
    DocumentPath: '',
    ValidationDate: '',
    TotalChecks: checkResults.length,
    PassedChecks: 0,
    FailedChecks: 0,
    WarningChecks: 0,
    TotalIssues: checkResults.reduce((sum, check) => sum + (check.Issues?.length || 0), 0),
    CriticalIssues: 0,
    ErrorIssues: 0,
    WarningIssues: 0,
    InfoIssues: 0,
    IsExportReady: false,
    CheckResults: checkResults,
    TotalExecutionTime: '',
    IssuesByCategory: {},
  };

  const candidates = buildValidationConditionCandidateItems(candidateReport);
  const resolutionsByIssue = new Map<ValidationIssue, ValidationConditionResolution[]>();

  for (const candidate of candidates) {
    const resolution = resolveCandidateByConditionSets(candidate, publishedSets);
    const existing = resolutionsByIssue.get(candidate.rawIssue) || [];
    existing.push(resolution);
    resolutionsByIssue.set(candidate.rawIssue, existing);
  }

  return checkResults.map((check) => {
    const nextIssues = (check.Issues || []).flatMap((issue) => {
      const issueResolutions = resolutionsByIssue.get(issue) || [];
      if (issueResolutions.length === 0) {
        return [issue];
      }

      const allExcluded = issueResolutions.every((resolution) => resolution.outcome === 'exclude');
      if (allExcluded) {
        return [];
      }

      const nextSeverity = resolveIssueSeverityOverride(issue, issueResolutions);
      if (nextSeverity === issue.Severity) {
        return [issue];
      }

      return [
        {
          ...issue,
          Severity: nextSeverity,
        },
      ];
    });

    return {
      ...check,
      Issues: nextIssues,
      IssueCount: nextIssues.length,
      HasIssues: nextIssues.length > 0,
      Status: resolveCheckStatusFromIssues(nextIssues),
    };
  });
}

export function applyCustomValidationPointsToCheckResults(params: {
  checkResults: ValidationCheckResult[] | undefined;
  conditionSets: ValidationConditionSet[];
  customValidationPoints: ValidationDefinition[];
  documentName?: string;
}): ValidationCheckResult[] {
  const { checkResults, conditionSets, customValidationPoints, documentName } = params;
  const baseChecks = checkResults ?? [];
  const activeCustomValidations = customValidationPoints.filter(
    (validation) => validation.IsEnabled && validation.Id >= CUSTOM_VALIDATION_ID_BASE
  );

  if (activeCustomValidations.length === 0 || baseChecks.length === 0) {
    return baseChecks;
  }

  const candidateReport: PcfValidationReport = {
    DocumentName: documentName || 'Current model',
    DocumentPath: '',
    ValidationDate: '',
    TotalChecks: baseChecks.length,
    PassedChecks: 0,
    FailedChecks: 0,
    WarningChecks: 0,
    TotalIssues: baseChecks.reduce((sum, check) => sum + (check.Issues?.length || 0), 0),
    CriticalIssues: 0,
    ErrorIssues: 0,
    WarningIssues: 0,
    InfoIssues: 0,
    IsExportReady: false,
    CheckResults: baseChecks,
    TotalExecutionTime: '',
    IssuesByCategory: {},
  };
  const candidates = buildValidationConditionCandidateItems(candidateReport);

  if (candidates.length === 0) {
    return baseChecks;
  }

  const customChecks = activeCustomValidations.flatMap((validation) => {
    const publishedSets = resolvePublishedConditionSets(conditionSets)
      .filter((conditionSet) => !conditionSet.archived)
      .filter((conditionSet) => conditionSet.version?.enabled)
      .filter((conditionSet) => resolvePublishedConditionSetValidationId(conditionSet) === validation.Id)
      .sort((left, right) => (left.version?.priority || 0) - (right.version?.priority || 0));

    if (publishedSets.length === 0) {
      return [];
    }

    const generatedIssues = candidates.flatMap((candidate) => {
      const matchingSet = publishedSets.find(
        (conditionSet) =>
          conditionSet.targetKey === candidate.targetKey &&
          conditionSet.version &&
          evaluateConditionDraftForCandidate(conditionSet.version, candidate).matched
      );
      if (!matchingSet?.version) {
        return [];
      }

      const severity = resolveCustomValidationSeverity(validation);
      const matchedRule = buildHumanReadableConditionSummary(matchingSet.version);
      const sourceIssue = candidate.rawIssue;

      return [
        {
          ...sourceIssue,
          ValidationId: validation.Id,
          ValidationTitle: validation.Title,
          Severity: severity,
          Message: `${validation.Title}: ${candidate.label} matched ${matchingSet.version.name}.`,
          Category: validation.Category || sourceIssue.Category,
          Recommendation:
            validation.Description ||
            `Review ${candidate.label} because it matched the custom validation rule.`,
          AdditionalData: {
            ...(sourceIssue.AdditionalData || {}),
            customValidation: 'true',
            sourceValidationId: String(sourceIssue.ValidationId),
            sourceValidationTitle: sourceIssue.ValidationTitle,
            conditionSetId: matchingSet.id,
            conditionSetName: matchingSet.version.name,
            matchedRule,
          },
        } satisfies ValidationIssue,
      ];
    });

    const deduplicatedIssues = Array.from(
      new Map(
        generatedIssues.map((issue) => [
          `${issue.ElementIds.join(',')}::${issue.Message}::${issue.AdditionalData?.conditionSetId || ''}`,
          issue,
        ])
      ).values()
    );

    return [
      {
        ValidationId: validation.Id,
        Title: validation.Title,
        Description: validation.Description,
        Status: resolveCheckStatusFromIssues(deduplicatedIssues),
        Issues: deduplicatedIssues,
        IssueCount: deduplicatedIssues.length,
        ExecutionTime: 'client rule',
        HasIssues: deduplicatedIssues.length > 0,
      } satisfies ValidationCheckResult,
    ];
  });

  return [...baseChecks, ...customChecks];
}

export function buildEffectiveValidationCheckResults(params: {
  checkResults: ValidationCheckResult[] | undefined;
  conditionSets: ValidationConditionSet[];
  customValidationPoints: ValidationDefinition[];
  documentName?: string;
}): ValidationCheckResult[] {
  const conditionAdjustedChecks = applyPublishedConditionSetsToCheckResults({
    checkResults: params.checkResults,
    conditionSets: params.conditionSets,
    documentName: params.documentName,
    customValidationIds: params.customValidationPoints.map((validation) => validation.Id),
  });

  return applyCustomValidationPointsToCheckResults({
    checkResults: conditionAdjustedChecks,
    conditionSets: params.conditionSets,
    customValidationPoints: params.customValidationPoints,
    documentName: params.documentName,
  });
}

export function resolvePublishedVsDraftImpact(params: {
  candidates: ValidationConditionCandidateItem[];
  conditionSets: ValidationConditionSet[];
  selectedConditionSetId: string | null;
}) {
  const { candidates, conditionSets, selectedConditionSetId } = params;

  const publishedSets = resolvePublishedConditionSets(conditionSets);
  const draftSets = conditionSets.map((conditionSet) => ({
    ...conditionSet,
    version: conditionSet.id === selectedConditionSetId ? conditionSet.draft : conditionSet.published,
  }));

  const publishedResolutions = candidates.map((candidate) =>
    resolveCandidateByConditionSets(candidate, publishedSets)
  );
  const draftResolutions = candidates.map((candidate) =>
    resolveCandidateByConditionSets(candidate, draftSets)
  );

  const changedItems = draftResolutions.filter((draftResolution, index) => {
    const publishedResolution = publishedResolutions[index];
    return (
      draftResolution.outcome !== publishedResolution.outcome ||
      draftResolution.winningConditionSetId !== publishedResolution.winningConditionSetId ||
      draftResolution.severity !== publishedResolution.severity
    );
  });

  return {
    publishedResolutions,
    draftResolutions,
    changedItems,
    matchedItems: draftResolutions.filter((resolution) => resolution.matched),
    excludedItems: draftResolutions.filter((resolution) => resolution.outcome === 'exclude'),
    newlyExcludedItems: draftResolutions.filter((resolution, index) => {
      const publishedResolution = publishedResolutions[index];
      return publishedResolution.outcome !== 'exclude' && resolution.outcome === 'exclude';
    }),
    newlyIncludedItems: draftResolutions.filter((resolution, index) => {
      const publishedResolution = publishedResolutions[index];
      return publishedResolution.outcome === 'exclude' && resolution.outcome !== 'exclude';
    }),
    severityChangedItems: draftResolutions.filter((resolution, index) => {
      const publishedResolution = publishedResolutions[index];
      return resolution.severity !== publishedResolution.severity;
    }),
  };
}

export function detectConditionSetConflicts(params: {
  conditionSet: ValidationConditionSet;
  candidates: ValidationConditionCandidateItem[];
  siblings: ValidationConditionSet[];
}) {
  const { conditionSet, candidates, siblings } = params;
  const conflicts: ValidationConditionConflict[] = [];
  const relevantCandidates = candidates.filter((candidate) => candidate.targetKey === conditionSet.targetKey);
  const selfEvaluationMatches = relevantCandidates.filter((candidate) =>
    evaluateConditionDraftForCandidate(conditionSet.draft, candidate).matched
  );

  if (selfEvaluationMatches.length === 0) {
    conflicts.push({
      id: `${conditionSet.id}-never-matches`,
      level: 'warning',
      title: 'Condition never matches',
      description: 'The current draft does not match any sampled validation items.',
      scope: 'builder',
    });
  }

  if (
    relevantCandidates.length > 0 &&
    selfEvaluationMatches.length === relevantCandidates.length &&
    conditionSet.draft.outcome.type === 'exclude'
  ) {
    conflicts.push({
      id: `${conditionSet.id}-too-broad`,
      level: 'error',
      title: 'Exclusion is too broad',
      description: 'This draft excludes every sampled item in the selected validation target.',
      scope: 'outcome',
    });
  }

  const incompleteClauseIds = conditionSet.draft.groups.flatMap((group) =>
    group.clauses
      .filter(
        (clause) =>
          (clause.operator === 'equals' ||
            clause.operator === 'contains' ||
            clause.operator === 'startsWith' ||
            clause.operator === 'matches' ||
            clause.operator === 'notEquals') &&
          clause.value.trim().length === 0
      )
      .map((clause) => clause.id)
  );

  if (incompleteClauseIds.length > 0) {
    conflicts.push({
      id: `${conditionSet.id}-incomplete-clause`,
      level: 'warning',
      title: 'Incomplete clause',
      description: 'One or more clauses use an operator that requires a value but the value is empty.',
      scope: 'clause',
      clauseIds: incompleteClauseIds,
    });
  }

  const higherPriorityOverlap = siblings.some((sibling) => {
    if (sibling.id === conditionSet.id || sibling.archived || !sibling.published?.enabled) {
      return false;
    }
    if (sibling.targetKey !== conditionSet.targetKey) {
      return false;
    }
    if ((sibling.published?.priority || 0) >= conditionSet.draft.priority) {
      return false;
    }

    return selfEvaluationMatches.some((candidate) =>
      evaluateConditionDraftForCandidate(sibling.published as ValidationConditionSetDraft, candidate).matched
    );
  });

  if (higherPriorityOverlap) {
    conflicts.push({
      id: `${conditionSet.id}-shadowed`,
      level: 'warning',
      title: 'Higher-priority overlap detected',
      description: 'A higher-priority published condition set already matches part of this draft scope.',
      scope: 'group',
      groupId: conditionSet.draft.groups[0]?.id,
    });
  }

  return conflicts;
}

export function buildHumanReadableConditionSummary(draft: ValidationConditionSetDraft): string {
  const groupSummary = draft.groups
    .map((group) =>
      group.clauses
        .map((clause) => buildConditionClauseLabel(clause))
        .join(group.logic === 'all' ? ' and ' : ' or ')
    )
    .join(draft.rootLogic === 'all' ? ' and ' : ' or ');

  const defaultOutcome =
    draft.defaultOutcome === 'include'
      ? 'If no condition matches, continue validating.'
      : 'If no condition matches, exclude from validation.';

  return `${buildOutcomeLabel(draft.outcome)} when ${groupSummary || 'no conditions are defined yet'}. Priority ${draft.priority}. ${defaultOutcome}`;
}

export function explainCandidateAgainstDraft(
  draft: ValidationConditionSetDraft,
  candidate: ValidationConditionCandidateItem
) {
  const evaluation = evaluateConditionDraftForCandidate(draft, candidate);
  const passedClauses = evaluation.groupResults.flatMap((group) =>
    group.clauseResults.filter((result) => result.passed).map((result) => result.label)
  );
  const failedClauses = evaluation.groupResults.flatMap((group) =>
    group.clauseResults.filter((result) => !result.passed).map((result) => result.label)
  );
  const finalOutcome = evaluation.matched
    ? buildOutcomeLabel(draft.outcome)
    : buildDefaultOutcomeLabel(draft.defaultOutcome);

  return {
    evaluation,
    passedClauses,
    failedClauses,
    matched: evaluation.matched,
    ruleResult: evaluation.matched ? 'passed' : 'failed',
    finalOutcome,
    groups: evaluation.groupResults.map((group) => ({
      id: group.groupId,
      logic: group.logic,
      passed: group.passed,
      reason: group.reason,
      clauses: group.clauseResults,
    })),
    summary: `Rule result: ${evaluation.matched ? 'passed' : 'failed'}. Final outcome: ${finalOutcome}.`,
  };
}

export function buildCustomValidationRuleSummary(draft: ValidationConditionSetDraft): string {
  const groupSummary = draft.groups
    .map((group) =>
      group.clauses
        .map((clause) => buildConditionClauseLabel(clause))
        .join(group.logic === 'all' ? ' and ' : ' or ')
    )
    .join(draft.rootLogic === 'all' ? ' and ' : ' or ');

  return `Create an issue when ${groupSummary || 'no conditions are defined yet'}. Priority ${draft.priority}.`;
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export interface BuildPublishStateLabelInput {
  publishBlocked: boolean;
  blockingClauseCount: number;
  blockingGroupCount: number;
  conflictErrorCount: number;
  conflictWarningCount: number;
  warningCount: number;
}

export function buildPublishStateLabel(input: BuildPublishStateLabelInput): string {
  const {
    publishBlocked,
    blockingClauseCount,
    blockingGroupCount,
    conflictErrorCount,
    conflictWarningCount,
    warningCount,
  } = input;

  const blockingParts = [
    blockingClauseCount > 0 ? pluralize(blockingClauseCount, 'blocking clause error') : null,
    blockingGroupCount > 0 ? pluralize(blockingGroupCount, 'blocking group error') : null,
    conflictErrorCount > 0 ? pluralize(conflictErrorCount, 'blocking conflict') : null,
  ].filter(Boolean);
  const warningParts = [
    conflictWarningCount > 0 ? pluralize(conflictWarningCount, 'conflict warning') : null,
    warningCount > 0 ? pluralize(warningCount, 'builder warning') : null,
  ].filter(Boolean);

  if (publishBlocked) {
    return `Publish blocked: ${blockingParts.join(', ')}${warningParts.length > 0 ? ` · ${warningParts.join(', ')}` : ''}`;
  }
  if (warningParts.length > 0) {
    return `Publish ready · ${warningParts.join(', ')}`;
  }
  return 'Publish ready';
}
